import { normalizeKey, parseChordSymbol, transposeKeyName } from "../../../shared/chords";

const PITCH: Record<string, number> = {
  C: 0,
  "B#": 0,
  "C#": 1,
  Db: 1,
  D: 2,
  "D#": 3,
  Eb: 3,
  E: 4,
  Fb: 4,
  F: 5,
  "E#": 5,
  "F#": 6,
  Gb: 6,
  G: 7,
  "G#": 8,
  Ab: 8,
  A: 9,
  "A#": 10,
  Bb: 10,
  B: 11,
  Cb: 11,
};

const DEGREE = ["1", "b2", "2", "b3", "3", "4", "b5", "5", "b6", "6", "b7", "7"];

export type ChartMode = "stacked" | "inline" | "lyrics" | "chords" | "numbers";

export const CHART_MODES: Array<{ id: ChartMode; label: string }> = [
  { id: "stacked", label: "Chords above lyrics" },
  { id: "inline", label: "Chords inline" },
  { id: "lyrics", label: "Lyrics only" },
  { id: "chords", label: "Chords only" },
  { id: "numbers", label: "Nashville numbers" },
];

export function safeKey(key: string, fallback = "C"): string {
  try {
    return normalizeKey(key);
  } catch {
    try {
      return normalizeKey(fallback);
    } catch {
      return "C";
    }
  }
}

export function pitchOf(key: string): number {
  const canonical = safeKey(key);
  const root = canonical.endsWith("m") ? canonical.slice(0, -1) : canonical;
  const pitch = PITCH[root];
  if (pitch === undefined) throw new Error(`Unknown key: ${key}`);
  return pitch;
}

export function semitoneGap(fromKey: string, toKey: string): number {
  return (pitchOf(toKey) - pitchOf(fromKey) + 12) % 12;
}

export function concertKey(fromKey: string, semitones: number): string {
  try {
    return transposeKeyName(fromKey, semitones);
  } catch {
    return safeKey(fromKey);
  }
}

export function writtenKey(chart: string, fallback: string): string {
  const match = chart.match(/\{\s*key\s*:\s*([^}]+)\}/i);
  const raw = match?.[1]?.trim() ?? "";
  return raw ? safeKey(raw, fallback) : safeKey(fallback);
}

export function toNashville(symbol: string, key: string): string {
  const parsed = parseChordSymbol(symbol);
  if (!parsed) return symbol;
  let tonic = 0;
  try {
    tonic = pitchOf(key);
  } catch {
    return symbol;
  }
  const degree = (root: string) => DEGREE[((PITCH[root] ?? 0) - tonic + 12) % 12] ?? root;
  const bass = parsed.bass ? `/${degree(parsed.bass)}` : "";
  return `${degree(parsed.root)}${parsed.quality}${bass}`;
}

export function beatsInMeter(meter: string | null | undefined): number {
  const match = /^(\d{1,2})\//.exec(meter ?? "");
  const beats = Number(match?.[1] ?? 4);
  if (!Number.isInteger(beats) || beats < 1 || beats > 16) return 4;
  return beats;
}

export function parseTags(value: string): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of value.split(",")) {
    const tag = part.trim().slice(0, 40);
    if (!tag || seen.has(tag.toLowerCase())) continue;
    seen.add(tag.toLowerCase());
    tags.push(tag);
    if (tags.length === 24) break;
  }
  return tags;
}

export function standHref(id: string, itemId?: string): string {
  const query = itemId ? `?item=${encodeURIComponent(itemId)}` : "";
  return `/stand/${encodeURIComponent(id)}${query}`;
}
